export interface CategoryMeta {
  id: string;
  name: string;
  en: string;
  slogan: string;
  desc: string;
  icon: string;
  chip: string;
  soft: string;
  gradient: string;
}

export interface Article {
  id: number;
  slug: string;
  categoryId: string;
  title: string;
  excerpt: string;
  cover: string;
  coverAlt: string;
  author: string;
  date: string;
  heat: number;
  likeCount: number;
  commentCount: number;
  tags: string[];
  readMin: number;
  pullquote: string;
  content: string[];
}

export const categories: CategoryMeta[] = [
  {
    id: 'celebrities',
    name: '风云人物',
    en: 'Campus Icons',
    slogan: '这届校园红人，都有两把刷子',
    desc: '盘点那些凭实力或凭魅力出圈的校园红人——从自习室一哥到广播台金嗓子，看看他们是怎么一步步成为全校都知道的名字。',
    icon: 'ri-user-star-line',
    chip: 'bg-accent-100 text-accent-800',
    soft: 'bg-accent-50',
    gradient: 'from-accent-400 to-primary-400',
  },
  {
    id: 'monitor',
    name: '班长学委',
    en: 'Class Officers',
    slogan: '班干部的隐藏副本，比你想的有料',
    desc: '点名册、Excel、班会……班干部们表面一本正经，私下的「班务经营学」和名场面才是真宝藏。',
    icon: 'ri-clipboard-line',
    chip: 'bg-secondary-100 text-secondary-800',
    soft: 'bg-secondary-50',
    gradient: 'from-secondary-400 to-primary-400',
  },
  {
    id: 'clubs',
    name: '社团名场面',
    en: 'Club Moments',
    slogan: '社团一出手，热搜抖三抖',
    desc: '招新、游园、决赛、快闪……社团的高光与翻车瞬间全收录，看看这些大学生都是怎么把社团玩出花的。',
    icon: 'ri-sparkling-2-line',
    chip: 'bg-primary-100 text-primary-800',
    soft: 'bg-primary-50',
    gradient: 'from-primary-400 to-accent-400',
  },
  {
    id: 'grad',
    name: '毕业照黑历史',
    en: 'Graduation Flashbacks',
    slogan: '学士帽飞起的瞬间，青春开始掉线',
    desc: '毕业照的尽头是表情包。那些被风吹起的学士帽、全班cosplay、宿管阿姨红了的眼眶，都是青春最好的注脚。',
    icon: 'ri-graduation-cap-line',
    chip: 'bg-cream-200 text-ink',
    soft: 'bg-cream-100',
    gradient: 'from-accent-400 to-secondary-400',
  },
  {
    id: 'contrast',
    name: '反差瞬间',
    en: 'Contrast Cut',
    slogan: '人前人后两副面孔，才是快乐源泉',
    desc: '拿国奖的学霸晚上去乐队打鼓、高冷队长私藏表情包……专治「刻板印象」，越反差越快乐。',
    icon: 'ri-toggle-line',
    chip: 'bg-primary-100 text-primary-800',
    soft: 'bg-primary-50',
    gradient: 'from-secondary-400 to-accent-400',
  },
];

export const articles: Article[] = [
  {
    id: 1,
    slug: 'library-wang-hot-seat',
    categoryId: 'celebrities',
    title: '把图书馆自习拍成综艺的「自习室一哥」，全校都想坐他旁边',
    excerpt: '坐他对面每天都是现场直播：泡面、保温杯、无声点头三件套，愣是把枯燥自习拍成了下饭综艺。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20focused%20male%20college%20student%20studying%20at%20a%20library%20desk%20with%20laptop%20and%20coffee%20cup%2C%20warm%20coral%20and%20amber%20color%20palette%2C%20playful%20editorial%20zine%20style%2C%20clean%20cream%20background%2C%20cute%20campus%20scene&width=800&height=560&seq=cover01&orientation=landscape',
    coverAlt: '自习室一哥在图书馆认真自习的卡通插画',
    author: '西瓜放映机',
    date: '2026-09-08',
    heat: 9821,
    likeCount: 2314,
    commentCount: 412,
    tags: ['风云人物', '自习室', '当代大学生'],
    readMin: 4,
    pullquote: '他研发出「保温杯战术」之后，方圆两排的座位都成了风水宝地。',
    content: [
      '江湖人称「自习室一哥」的这位，真名无人知晓，但只要你常驻图书馆三楼靠窗那排，就一定见过他：一个保温杯、一摞高到下巴的复习资料、外加一颗在任何噪音里都能岿然不动的头。',
      '一哥的自习有一套完整「节目流程」。先是稳稳落座，把保温杯放在左手边固定位置；接着掏出五色便利贴把今天的目标按优先级排好，像综艺里那种任务清单；然后是长达二十分钟的无声点头与碎碎念，配上面部微表情管理，堪称一个人撑起一台「沉浸式学习直播」。',
      '真正让他封神的是「保温杯战术」。有人统计过，自从他研发了保温杯+纸巾+便签的桌面三件套占位法后，方圆两排的座位一到期末就成了抢手货。同学们宁可多走一层楼，也要坐在他的「势力范围」里沾沾学习氛围。',
      '后来有热心同学把他自习的日常做成了表情包合集，配文「学霸的一天」「书山有路杯中月」，在班级群和年级群里疯转。一哥本人倒是很淡定，只是默默把表情包存进了「激励自己」的收藏夹。',
      '毕业那年他考研上岸，把用了四年的保温杯留在原座位，贴了张便签：「接力棒交给下一位自习室扛把子。」后来真有学弟接棒，图书馆三楼的自习「综艺」还在继续更新。',
    ],
  },
  {
    id: 2,
    slug: 'radio-golden-voice',
    categoryId: 'celebrities',
    title: '一人撑起广播台的「金嗓子学姐」，毕业当天把全校整哭了',
    excerpt: '四年如一日准点开麦，从午间播报到晚间点歌，声音是整座校园的「情绪背景乐」。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20confident%20female%20college%20student%20hosting%20campus%20radio%20with%20headphones%20and%20microphone%2C%20warm%20coral%20amber%20and%20rose%20palette%2C%20cheerful%20editorial%20zine%20style%2C%20clean%20cream%20background%2C%20campus%20broadcast%20booth&width=800&height=560&seq=cover02&orientation=landscape',
    coverAlt: '金嗓子学姐在广播台主持节目的卡通插画',
    author: '爆料小分队',
    date: '2026-09-06',
    heat: 9012,
    likeCount: 2056,
    commentCount: 380,
    tags: ['风云人物', '广播台', '毕业'],
    readMin: 5,
    pullquote: '她说自己的声音很普通，可四年来全校都听习惯了这份「普通」。',
    content: [
      '每到中午十二点整，校园上空总会准时响起一个声音：「各位同学中午好，欢迎收听今天的校园广播，我是你们的播音员阿紫。」四年了，雷打不动，连期末周都不缺席。',
      '阿紫是校广播台唯一一位「全勤播音」。台里人来人往，部长换了一茬又一茬，只有她从大一做到大四，午间新闻、晚间点歌、运动会实况，全靠这一把嗓子扛下来。学弟学妹私底下都叫她「铁嗓学姐」。',
      '她真正的魔法不在技巧，而在懂得把声音用在对的时间。期末周她会多放十分钟轻音乐；运动会中场她能用一句话把全场的疲惫喊走；每年迎新，她都让新生第一次在陌生校园里找到「被接住」的感觉。',
      '毕业前最后一期节目，她像往常一样开麦，读到结尾时停了一下：「谢谢大家听了四年，阿紫的声音要下线啦。」那天，无数人在宿舍里安静地听完了最后一秒，评论区被「别走」「泪目」「明年还会回来的对吧」刷屏。',
      '后来台里给她的工位留了很久。她说等哪天回母校出差，还要回来录一期「校友点歌」。声音会暂时下线，但习惯会在每个整点准时想念。',
    ],
  },
  {
    id: 3,
    slug: 'social-phobia-host',
    categoryId: 'celebrities',
    title: '从「重度社恐」到迎新晚会主持人，大三学长只用了一个学期',
    excerpt: '曾在自我介绍时紧张到忘了自己叫什么，如今却能站在千人舞台抖包袱——改变没有捷径，只有一次次的「硬着头皮」。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20shy%20young%20man%20holding%20a%20microphone%20on%20stage%20becoming%20confident%20host%2C%20spotlight%20and%20audience%20in%20background%2C%20warm%20coral%20and%20amber%20palette%2C%20editorial%20zine%20style%2C%20clean%20cream%20background%2C%20campus%20event&width=800&height=560&seq=cover03&orientation=landscape',
    coverAlt: '社恐学长成长为迎新晚会主持人的卡通插画',
    author: '北区观察员',
    date: '2026-09-03',
    heat: 8456,
    likeCount: 1874,
    commentCount: 298,
    tags: ['风云人物', '成长', '迎新晚会'],
    readMin: 5,
    pullquote: '「紧张没用，那就让紧张替你打工。」',
    content: [
      '如果三年前有人告诉阿哲他会站在千人舞台上当主持人，他一定觉得是玩笑。大一的他连在班里做自我介绍都会紧张到忘记自己叫什么，最后憋出一句「我……我姓哲」，成为全班半年的笑点。',
      '转机发生在一次偶然。室友拉他去参加学院的主持人海选「凑个数」，他在候场区抖了十分钟，上台却意外把一段稿子讲得很有代入感。评委一句「你很有镜头感」让他动了心。',
      '之后他给自己定了个「丢人计划」：每周至少主动举手发言一次、报名一次小活动。他把紧张当成训练素材，专门记录自己每次「又脸红了」的场景，一点点把自己推向镜头和人群。',
      '到了大三迎新晚会，他已经能站在台上自如抖包袱，还能在节目冷场时三秒救场。彩排时他说了一句让很多人记住的话：「紧张没用，那就让紧张替你打工。」',
      '现在的他偶尔还会在自我介绍时习惯性想绕开「姓哲」这个梗，但早就不怕了。他说社恐不是缺点，只是需要一块更大的舞台来练习勇敢。',
    ],
  },
  {
    id: 4,
    slug: 'class-monitor-roster',
    categoryId: 'monitor',
    title: '班长的十页点名册：我们班考勤最卷的秘密被曝光了',
    excerpt: '从迟到时间精确到分钟，到请假事由手写备注，这本被翻到起毛边的点名册，记录了一个班长的强迫症与责任心。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20diligent%20male%20class%20monitor%20writing%20attendance%20in%20a%20notebook%20at%20a%20classroom%20desk%2C%20students%20chatting%20in%20background%2C%20warm%20rose%20and%20coral%20palette%2C%20cute%20editorial%20zine%20style%2C%20clean%20cream%20background&width=800&height=560&seq=cover04&orientation=landscape',
    coverAlt: '班长认真记录班级点名册的卡通插画',
    author: '三号楼情报员',
    date: '2026-09-07',
    heat: 8733,
    likeCount: 1921,
    commentCount: 335,
    tags: ['班长', '考勤', '班级日常'],
    readMin: 4,
    pullquote: '点名册第 47 页的空白处，班长写着一行小字：「大家都很棒，加油。」',
    content: [
      '我们班的考勤能保持四年零失联记录，全靠班长老周那本十页厚的点名册。外人以为那只是记个到，直到某天它不小心从讲台掉下来，翻开的瞬间全班沉默了。',
      '册子里每一条都精细到离谱：迟到的时间精确到分钟，谁在哪节课打瞌睡都有侧记，请假事由被他分类成「家事/病假/突发事件」并配了手写小备注，甚至还有一栏「本周进步」专门记录谁出勤变好了。',
      '最扎心的是第 47 页的空白处，老周写着一行小字：「大家都很棒，加油。」原来他查岗不是因为爱管闲事，而是把每个同学的名字都认真记在心里，谁要是连着几天状态不对，他第二天就会私聊问一句「要不要聊聊」。',
      '册子曝光后，班里一时热闹：有人调侃这是「班主任代班实录」，也有人认真道谢——因为期末那阵子，老周靠着这本册子帮几个总踩点到课的同学盯住了风险科目。',
      '老周对此的回应很朴素：「点名是工作，记住每个人是顺手的事。」后来这本点名册被他好好收藏起来，说是要留给以后当老师的自己当教案。',
    ],
  },
  {
    id: 5,
    slug: 'study-officer-excel',
    categoryId: 'monitor',
    title: '学委的 Excel 究竟有多强？期末复习资料全是「代码级」分类',
    excerpt: '别人划重点靠人脉，学委划重点靠公式。一张表能把考点按出题概率排序，堪称期末救命的「外挂」。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20smart%20female%20study%20officer%20organizing%20colorful%20spreadsheets%20and%20formula%20charts%20at%20a%20desk%2C%20study%20notes%20and%20books%20around%2C%20warm%20coral%20amber%20rose%20palette%2C%20cute%20editorial%20zine%20style%2C%20clean%20cream%20background&width=800&height=560&seq=cover05&orientation=landscape',
    coverAlt: '学委用 Excel 整理复习资料的卡通插画',
    author: '自习室雷达',
    date: '2026-09-05',
    heat: 8320,
    likeCount: 1766,
    commentCount: 287,
    tags: ['学委', 'Excel', '期末'],
    readMin: 4,
    pullquote: '她把「划重点」做成了一套工程：公式、配色、概率排序，一个都不少。',
    content: [
      '每到期末，我们班的复习氛围就莫名「卷」出一个固定环节：等学委小鹿发资料。不是因为她消息最灵通，而是她发的东西是真的能救命。',
      '小鹿有一张自建的「考点雷达表」。别人划重点靠找老师，她靠公式：把历年真题、课堂强调、PPT 出现次数录入 Excel，再用条件格式把出题概率高的章节标红。据说她还会给考点分「必考/常考/可能考」三档，连配色都是精心调的。',
      '更绝的是她的资料命名。「2026 高数期末·真题+笔记+公式卡·已校对v3」，一份资料里还能看到她的批注：哪道题是哪位老师的风格、哪章有隐藏考点，条理清晰得不像人整理的。',
      '班级群流传着一句话：「挂科边缘不用怕，小鹿资料保平安。」有人说她这份认真更像工程师思维，她却淡淡回一句：「你们省时间，我就省点催人交作业的力气。」',
      '期末后大家组队请她吃饭，她拿出手机当场演示了表格自动化——一分钟后把全班这学期的资料汇总得明明白白。那一刻，全桌人默默给她倒了杯饮料：这哪是学委，这是班级的数据中台。',
    ],
  },
  {
    id: 6,
    slug: 'discipline-inspector-gaze',
    categoryId: 'monitor',
    title: '纪律委员的「死亡凝视」名场面，全班沉默了整整三十秒',
    excerpt: '没有一句呵斥，仅凭一个回头，就让后排炸开锅的教室瞬间安静如自习。当代班级控场的终极武器，非凝视莫属。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20serious%20calm%20female%20discipline%20monitor%20looking%20sternly%20across%20a%20quiet%20classroom%20with%20silent%20students%2C%20warm%20rose%20and%20cream%20palette%2C%20playful%20editorial%20zine%20style%2C%20clean%20background%2C%20campus%20classroom%20scene&width=800&height=560&seq=cover06&orientation=landscape',
    coverAlt: '纪律委员用眼神控场教室的卡通插画',
    author: '爆青团编辑部',
    date: '2026-08-30',
    heat: 7654,
    likeCount: 1432,
    commentCount: 215,
    tags: ['纪律委员', '班级名场面', '控场'],
    readMin: 3,
    pullquote: '她没开口，但全班都收到了同一条信息：安静。',
    content: [
      '提到我们班的纪律委员阿音，先想起的不是她喊过多少次「安静」，而是一个眼神。那是高一晚自习，后排几人正聊得火热，整间教室吵得像菜市场。',
      '阿音没拍桌子、没吼人，只是慢慢回过头，用一副「你自己看着办」的表情扫了一眼后排。就这一眼，声音像被按了暂停键，现场安静了整整三十秒——连翻书的人都下意识放轻了动作。',
      '这段「死亡凝视」后来被同学做成表情包，配文从「你再讲一句试试」到「母爱般的平静警告」，在年级群里疯传。神奇的是，阿音本人看到后只淡定回了句：「眼神不累，嗓子不疼。」',
      '久而久之，班里形成一种默契：只要阿音回头，大家就会自动收敛。有同学调侃她是「移动静音键」，也有人在班会总结时认真说，这个眼神让全班少了很多无谓的喧闹。',
      '后来毕业聚餐，有人斗胆问阿音：「你那眼神是练出来的吗？」她笑了：「不是练的，是当纪律委员当久了，练出来的。」全班再次陷入熟悉的安静，然后笑成一团。',
    ],
  },
  {
    id: 7,
    slug: 'drama-club-recruitment',
    categoryId: 'clubs',
    title: '戏剧社把社团招新办成迷你音乐剧，现场尖叫直接破音',
    excerpt: '别人发传单，他们发「门票」。一支五分钟的原创音乐剧，让路过的新生集体排队报名。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20colorful%20drama%20club%20performing%20a%20mini%20musical%20on%20a%20sunny%20campus%20recruitment%20stage%2C%20happy%20students%20applauding%2C%20warm%20coral%20amber%20palette%2C%20vibrant%20editorial%20zine%20style%2C%20clean%20cream%20background&width=800&height=560&seq=cover07&orientation=landscape',
    coverAlt: '戏剧社在校园招新表演迷你音乐剧的卡通插画',
    author: '食堂小喇叭',
    date: '2026-09-04',
    heat: 9210,
    likeCount: 2140,
    commentCount: 366,
    tags: ['社团', '戏剧社', '招新'],
    readMin: 4,
    pullquote: '别家发传单，他们发「演出门票」；别家教新生，他们直接让人追剧。',
    content: [
      '社团招新季，别的摊位靠发传单和气球吸引人，戏剧社的摊位却里三层外三层围满了人——因为他们直接在路边支起小舞台，演了一出五分钟的原创迷你音乐剧。',
      '这出剧讲的是一个新生加入社团的成长故事，包袱密集、唱跳全开，结尾还设计了一个「加入我们」的互动桥段。路过的新生被剧情和氛围带着走，现场尖叫一度破音，有人甚至边看边问「这是学校请来的演出吗」。',
      '社长的安排也够狠：台上演完，台下立刻开「返场答疑」，把社团的排练日常、演出机会、团建内容编进谢幕词。原本只是路过看热闹的同学，散场时不少人手里已经捏着报名表。',
      '有新生后来在群里复盘：「本来只想围观，结果看完第一幕我就决定要报名了。」戏剧社的招新人数那周直接冲到全校第一，连辅导员都来问要不要把这段演出留到迎新晚会上加场。',
      '有人问社长为什么这么拼，他说：「招新不是拉人头，是把我们最快乐的样子演给新人看。他们愿意来，说明我们演对了。」',
    ],
  },
  {
    id: 8,
    slug: 'debate-finals-quiet',
    categoryId: 'clubs',
    title: '辩论队决赛现场，二辩学姐一句话让全馆瞬间安静',
    excerpt: '没有稿子，没有嘶吼，只用一段走心的总结陈词，就让三百人同时停下掌声听她说完。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20passionate%20female%20debater%20speaking%20at%20a%20podium%20during%20a%20campus%20debate%20finals%2C%20audience%20listening%20intently%2C%20warm%20amber%20and%20coral%20palette%2C%20dramatic%20editorial%20zine%20style%2C%20clean%20cream%20background&width=800&height=560&seq=cover08&orientation=landscape',
    coverAlt: '辩论队二辩学姐在决赛中发言的卡通插画',
    author: '辩论组外援',
    date: '2026-08-28',
    heat: 8120,
    likeCount: 1703,
    commentCount: 254,
    tags: ['社团', '辩论队', '决赛'],
    readMin: 4,
    pullquote: '「我们可以输掉一场比赛，但请不要输掉好好说话的能力。」',
    content: [
      '那天的辩论赛决赛辩题很接地气，现场气氛一度剑拔弩张。反方二辩、辩论队的学姐阿词，整场都没走「硬刚」路线，反而用节奏和逻辑一点点把观点铺开。',
      '最出圈的是她的总结陈词。前面的自由辩论刚结束，全场还嗡嗡作响，她站起来，没有照稿念，而是放慢语速讲了一段关于「表达的分寸」的话，讲到一半，全场三百人忽然安静下来，连鼓掌都停了，都在听她说完。',
      '她把那句「我们可以输掉一场比赛，但请不要输掉好好说话的能力」收进结尾时，馆里响起一阵迟到的、整齐的掌声。那场比赛的结果反而不那么重要了，因为这段「好好说话」早已赢得全场。',
      '赛后这段陈词被剪成音频在校园流传，有人把它设成自习时的「提神素材」。阿词却说其实她平时嘴很笨，只是辩得多了，才慢慢学会在激动时先让逻辑落地。',
      '新生辩论队招新时，很多人是冲着「想成为那样会好好说话的人」来的。阿词在面试里笑着说：「好，先从今天开始，把每句话说完、说清楚。」',
    ],
  },
  {
    id: 9,
    slug: 'hanfu-festival-flashmob',
    categoryId: 'clubs',
    title: '汉服社游园会：一场「沉浸式国风快闪」让路人集体围观',
    excerpt: '猜灯谜、投壶、汉服巡游……原本只是社团活动，硬是被办成了全校打卡的「限定主题园」。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20college%20students%20in%20traditional%20hanfu%20clothing%20enjoying%20a%20garden%20festival%20with%20lanterns%20and%20archery%20games%2C%20warm%20amber%20and%20coral%20palette%2C%20elegant%20yet%20playful%20editorial%20zine%20style%2C%20clean%20cream%20background&width=800&height=560&seq=cover09&orientation=landscape',
    coverAlt: '汉服社学生参加国风游园会的卡通插画',
    author: '国风小分队',
    date: '2026-08-25',
    heat: 8560,
    likeCount: 1988,
    commentCount: 276,
    tags: ['社团', '汉服社', '游园会'],
    readMin: 5,
    pullquote: '本来只准备了三个小时的游园，被围观人群硬生生续到了天黑。',
    content: [
      '汉服社的游园会本是个常规活动：猜灯谜、投壶、做香囊，外加一段社团内部的巡游。谁也没想到，那天办着办着就成了全校的「限定主题园」。',
      '社员们身着各色汉服在校园主干道巡游，衣袂飘飘的瞬间引来无数路人驻足。有同学当场掏出手机，也有人边追着拍照边问「这是学校请来的剧组吗」。队伍越走越长，围观的人干脆跟着队伍一路打卡。',
      '原定的三个小时很快不够用——猜灯谜摊位前排起长队，投壶区甚至出现了「跨社团挑战赛」。汉服社临时加开两轮巡游，社长拿着小喇叭喊：「大家别急，园子我们不收门票，慢慢逛！」',
      '最暖的瞬间出现在傍晚。一位路过的学姐停下来，说自己本科毕业三年回来办事，看到熟悉的校园多了这样一群认真过节的人，眼眶有点热。她当场和社员合了影，说「这就是我记忆里大学该有的样子」。',
      '活动结束后，汉服社把大家的照片整理成「游园纪念册」，还贴心地标注了每件汉服形制的名字。有新生在群里说：「谢谢你们，让我第一次觉得传统文化离我这么近。」',
    ],
  },
  {
    id: 10,
    slug: 'grad-cap-wind',
    categoryId: 'grad',
    title: '毕业照黑历史第 01 期：那顶被风掀走的学士帽',
    excerpt: '摄影师喊「三、二、一、抛」的瞬间，风把帽子带偏了，也把我们定格成了青春里最真实的模样。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20happy%20graduates%20throwing%20caps%20in%20the%20air%20while%20one%20cap%20blows%20away%20in%20the%20wind%2C%20warm%20cream%20amber%20and%20coral%20palette%2C%20nostalgic%20and%20playful%20editorial%20zine%20style%2C%20clean%20background%2C%20campus%20graduation%20scene&width=800&height=560&seq=cover10&orientation=landscape',
    coverAlt: '毕业生抛学士帽时帽子被风吹走的卡通插画',
    author: '毕业季特别栏目',
    date: '2026-08-22',
    heat: 8990,
    likeCount: 2201,
    commentCount: 341,
    tags: ['毕业照', '黑历史', '青春'],
    readMin: 3,
    pullquote: '那顶被风吹走的帽子，成了我们毕业照里最「飞」的一笔。',
    content: [
      '每年六月，毕业照的经典项目就是集体抛学士帽。摄影师总会喊那句魔性的「三、二、一、抛——」，可风总是有自己的想法。',
      '这一期的主角是一位学长的学士帽。只听「嗖」一声，帽子不按剧本飞向天空，而是直奔身后的树丛。全班都愣住了，随即爆发出此起彼伏的笑声，镜头恰好把这张「抓帽名场面」拍了下来。',
      '事后大家翻照片才发现，这张意外反而成了最生动的一张——有人笑得弯了腰，有人追着帽子跑，原本规规矩矩的毕业照，因为这阵风多了满满的人味。',
      '学长后来把这张照片设成了自己的头像，说「毕业这天最怕的就是规规矩矩地告别，风吹走的帽子反而让告别轻快了一点。」他把帽子捡回来后，郑重地签上了全班的名字，压在行李箱里带去了新城市。',
      '「毕业照黑历史」栏目想说的其实很简单：那些没站稳的pose、飞偏的帽子、笑到模糊的脸，才最像我们拼尽全力的青春。',
    ],
  },
  {
    id: 11,
    slug: 'dorm-last-photo',
    categoryId: 'grad',
    title: '宿舍楼最后的合影：宿管阿姨悄悄红了眼眶',
    excerpt: '搬行李那天，没人喊集合，可大家还是回到楼下拍了一张合影——镜头里多了一个最想留住的人。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20graduating%20students%20taking%20a%20final%20group%20photo%20with%20a%20warm%20older%20dormitory%20manager%20lady%20in%20front%20of%20a%20dorm%20building%2C%20warm%20cream%20amber%20palette%2C%20touching%20editorial%20zine%20style%2C%20clean%20background%2C%20campus%20dorm%20scene&width=800&height=560&seq=cover11&orientation=landscape',
    coverAlt: '毕业生与宿管阿姨在宿舍楼下合影的卡通插画',
    author: '毕业季特别栏目',
    date: '2026-08-20',
    heat: 8720,
    likeCount: 1980,
    commentCount: 305,
    tags: ['毕业照', '宿管阿姨', '青春'],
    readMin: 4,
    pullquote: '阿姨说：「你们走了，这栋楼就安静了，我会不习惯的。」',
    content: [
      '离校那天，宿舍楼里上上下下都在搬行李。没人正式组织，可不知道是谁在群里发了句「楼下拍张照再走吧」，不一会儿，一个寝室、两个寝室，大家都陆续回到了楼下。',
      '有人提议把平时总帮他们开门、查寝、收快递的宿管阿姨也叫上。阿姨先是摆手说「我一个打杂的，拍什么照」，架不住同学们连哄带劝，最后不好意思地站到了队伍正中间。',
      '快门按下的前几秒，空气忽然安静下来。有人突然开口：「阿姨，谢谢你这四年。」话音没落，大家都跟着道谢。阿姨没说话，只是低头抹了一下眼睛，红着眼眶说：「你们走了，这栋楼就安静了，我会不习惯的。」',
      '那一瞬间，很多人才意识到，四年来在楼道里喊过无数次「记得断电」的阿姨，其实一直把他们当孩子一样惦记。有人默默红了眼，有人笑着把阿姨拉到镜头前比了个耶，用最轻松的方式藏起不舍。',
      '后来这张照片被洗出来，贴在宿舍楼的公告栏。新生入住时看到，总有人问「阿姨怎么眼睛红红的」。阿姨就说：「前两年送走一群臭小子，眼睛进沙子了呗。」可谁都看得出，那是在场所有人共同的青春。',
    ],
  },
  {
    id: 12,
    slug: 'freshman-senior-compare',
    categoryId: 'grad',
    title: '翻出大一 vs 大四对比照，这届毕业生的反差绝了',
    excerpt: '发型变了、气质变了、连「社恐值」都变了。大学四年到底改变了我们什么？答案都写在对比照里。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20split%20comparison%20of%20the%20same%20student%20as%20a%20shy%20freshman%20and%20a%20confident%20senior%20on%20campus%2C%20warm%20coral%20amber%20palette%2C%20playful%20editorial%20zine%20style%2C%20clean%20cream%20background%2C%20before%20and%20after%20graduation&width=800&height=560&seq=cover12&orientation=landscape',
    coverAlt: '大一与大四对比照中同一位学生的反差卡通插画',
    author: '毕业季特别栏目',
    date: '2026-08-18',
    heat: 9340,
    likeCount: 2267,
    commentCount: 388,
    tags: ['毕业照', '对比', '反差'],
    readMin: 4,
    pullquote: '照片里的不是「变了一个人」，而是那个终于敢做自己的自己。',
    content: [
      '毕业前，有位同学在班级群发起了一个「大一 vs 大四」挑战，让大家翻出四年前的照片和现在的自己拼在一起。一时间，相册、聊天记录、社团海报都被翻了个底朝天。',
      '对比照里藏满了笑点与泪点：有人大一顶着厚重的齐刘海、戴着黑框眼镜，大四烫了卷发、气场全开；有人从「拍照就躲镜头」变成「主动站在C位喊大家拍照」；还有人变化不大，却自信地标注「我只是更会爱自己了」。',
      '最戳人的是一位同学配的文字：「大一的我以为大学是去变成另一个人，现在才明白，大学只是把原本那个我找回来了。」这句话在群里引发了整整一晚的走心夜聊。',
      '大家这才发现，变化的从来不只是外表。那些熬夜赶DDL、鼓起勇气演讲、第一次组织活动、第一次在陌生城市独立生活的瞬间，都一点一点重塑了他们。',
      '栏目组把这组对比整理成了「四年修炼手册」，片尾放了一句祝福：愿每一个毕业生，都带着这四年攒下的勇气，去成为自己喜欢的样子。',
    ],
  },
  {
    id: 13,
    slug: 'scholar-drummer-dual-life',
    categoryId: 'contrast',
    title: '白天拿国奖的学霸，晚上在乐队打鼓打到飞起',
    excerpt: '奖学金答辩席上冷静从容，音乐节的鼓手位上却燃到爆炸。两副面孔无缝切换，这就是传说中「全方位优秀」。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20male%20student%20energetically%20playing%20drums%20at%20night%20showing%20a%20cool%20contrast%20to%20his%20nerdy%20study%20side%2C%20warm%20coral%20and%20amber%20palette%2C%20dynamic%20editorial%20zine%20style%2C%20clean%20cream%20background%2C%20campus%20music%20scene&width=800&height=560&seq=cover13&orientation=landscape',
    coverAlt: '白天学霸晚上打鼓的反差瞬间卡通插画',
    author: '反差观察局',
    date: '2026-09-01',
    heat: 9470,
    likeCount: 2310,
    commentCount: 401,
    tags: ['反差', '学霸', '乐队'],
    readMin: 4,
    pullquote: '「学习和打鼓一样，都要先把节奏稳住，再放开打。」',
    content: [
      '在大多数同学的印象里，阿恺是那个「永远拿国奖的学长」：答辩席上冷静从容、成绩单漂亮到没朋友，连熬夜学习都显得很体面。直到有次校园音乐节，大家才发现他还有另一面。',
      '当舞台灯光打下来，阿恺坐在鼓手位上，节奏一起，整个人瞬间换了气场——鼓槌翻飞、动作带风，原本「文气」的学长摇身一变成了燃爆全场的鼓手。台下的同班同学惊得面面相觑：「这真的是那个奖学金答辩说『谢谢老师』都脸红的阿恺？」',
      '其实他从大一开始就偷偷玩乐队，只是平时低调。他说乐队排练的时间都是他「挤」出来的：白天泡图书馆，晚上九点后去排练房打两小时鼓，周日固定雷打不动去livehouse观摩。',
      '「学习和打鼓其实很像，」阿恺说，「都得先把基础节奏稳住，才有余力放开去发挥。扎实的练习不会骗人。」他把打鼓的专注也带回了学习，效率出奇地高。',
      '音乐节之后，阿恺「学霸+鼓手」的双重身份在校园传开。有人调侃他是「反差天花板」，他倒是很淡定：「你们别神化我，我也就比你们多一个会流汗的爱好罢了。」',
    ],
  },
  {
    id: 14,
    slug: 'basketball-captain-stickers',
    categoryId: 'contrast',
    title: '场上高冷的篮球队长，私下竟是个「表情包收藏家」',
    excerpt: '球场上一句多余的话都不说，微信里却存满了各种魔性表情包。反差来得太快，队友都看傻了。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20tall%20cool%20basketball%20team%20captain%20looking%20serious%20on%20court%20next%20to%20a%20phone%20screen%20showing%20funny%20sticker%20emojis%2C%20warm%20secondary%20rose%20and%20amber%20palette%2C%20playful%20editorial%20zine%20style%2C%20clean%20cream%20background&width=800&height=560&seq=cover14&orientation=landscape',
    coverAlt: '高冷篮球队长私下收藏表情包的反差卡通插画',
    author: '篮板下的小迷弟',
    date: '2026-08-26',
    heat: 8567,
    likeCount: 1811,
    commentCount: 289,
    tags: ['反差', '篮球队', '表情包'],
    readMin: 3,
    pullquote: '「场上要稳住气势，表情包是气势的润滑剂。」',
    content: [
      '提到篮球队长阿北，多数人第一反应是「冷」：球场上不苟言笑，输了球也不多说话，赛后复盘时更是惜字如金。队里没人敢在他面前嘻嘻哈哈。',
      '直到某天训练间隙，副队长不小心瞥到阿北的手机屏幕，整个人当场笑趴——这位「高冷队长」的相册里，竟然收藏了满满几大本魔性表情包：什么「鸭鸭震惊」「猫猫叹气」「小狗摆烂」，应有尽有，比表情包大户还全。',
      '更绝的是，他居然还是个「斗图高手」。比赛赢了，他在群里发一只「骄傲的小狗」；队员受伤，他发「摸摸头」的抱抱猫；连教练偶尔说他两句，他也能甩个「知道了知道了」的鸭子敷衍包。',
      '反差被曝光后，队长的人设在队里彻底「崩塌」成另一副模样。有人问他为什么私下这么爱存表情包，阿北难得认真：「场上要稳住气势，表情包是气势的润滑剂。有些话不好直说，发个图大家都懂。」',
      '从此以后，篮球队群聊的画风一去不复返。阿北依旧是那个球场上冷静的队长，只是大家都知道——高冷是职业素养，表情包才是他的本体。',
    ],
  },
  {
    id: 15,
    slug: 'canteen-aunt-piano',
    categoryId: 'contrast',
    title: '食堂阿姨的隐藏技能：她或许才是校园里真正的钢琴大师',
    excerpt: '午休时间，食堂阿姨坐在角落的旧钢琴前弹了一曲，打饭的队伍瞬间安静了。',
    cover: 'https://readdy.ai/api/search-image?query=Flat%20vector%20illustration%20of%20a%20kind%20older%20female%20canteen%20worker%20playing%20an%20old%20piano%20in%20a%20sunny%20campus%20cafeteria%20while%20students%20watch%20in%20amazement%2C%20warm%20cream%20amber%20and%20coral%20palette%2C%20touching%20editorial%20zine%20style%2C%20clean%20background&width=800&height=560&seq=cover15&orientation=landscape',
    coverAlt: '食堂阿姨在食堂旧钢琴前演奏的卡通插画',
    author: '反差观察局',
    date: '2026-08-24',
    heat: 9230,
    likeCount: 2410,
    commentCount: 402,
    tags: ['反差', '食堂阿姨', '隐藏技能'],
    readMin: 4,
    pullquote: '「会一点，年轻的时候学过。现在弹给你听，是怕你们想家。」',
    content: [
      '食堂二楼角落有一架落灰多年的旧钢琴，平时没人弹，更像是装饰。直到有天中午打饭高峰期，一段流畅的钢琴声突然从角落响起，整个打饭队伍瞬间安静了下来。',
      '弹琴的不是音乐系学生，而是天天打菜的王阿姨。只见她微微俯身，指尖在旧琴键上走得很稳，弹的是一首大家耳熟能详的老歌旋律。有人端着餐盘忘了走，有人掏出手机录下这一幕。',
      '原来王阿姨年轻时学过几年钢琴，来食堂工作后，偶然发现这架闲置的琴，便常在午休时悄悄弹上两首。她不觉得这有什么特别，被问到时只是笑笑：「会一点，怕手生，偶尔练练。」',
      '后来食堂多了个不成文的默契：每天午休，若听到钢琴声，大家都会放轻脚步。有同学特意绕到二楼听一曲，说阿姨弹出的曲子「有种让人安静下来的力量」。',
      '有人问王阿姨为什么愿意弹给学生们听，她想了想说：「你们大老远来上学，吃饭的时候听到点熟悉的调子，就没那么想家了。」话音一落，围着的人里，好几个人悄悄别过了头。',
    ],
  },
];